import {th} from './th';

/**
 * Every message key in the surface, derived from the Thai dictionary.
 *
 * Thai is the source, so this type is `keyof typeof th`. Adding a key means
 * adding it to `th.ts` first; `en.ts` and `zh.ts` then fail to compile until
 * they carry it too.
 */
export type MessageKey = keyof typeof th;

/** Interpolation values for `{braces}` in a message. */
export type MessageParams = Record<string, string | number>;

/** A full dictionary. Every key, no exceptions — there is no fallback chain. */
export type Messages = Record<MessageKey, string>;

/**
 * The three languages at launch. **Not flags** — see `q2.th` / `q2.en` /
 * `q2.zh`, which name each language in its own script.
 */
export type Language = 'th' | 'en' | 'zh';

export const LANGUAGES: readonly Language[] = ['th', 'en', 'zh'] as const;

/** Thai first, always. A tourist venue may see more EN/CN, and it still is. */
export const DEFAULT_LANGUAGE: Language = 'th';

/** BCP 47 tags, for `OpenGuestSessionRequest.preferred_language` and `<html lang>`. */
export const BCP47: Record<Language, string> = {
  th: 'th',
  en: 'en',
  zh: 'zh-Hans',
};

/** Which field of a `LocalizedText` each language reads. */
export const LOCALIZED_FIELD = {
  th: 'th',
  en: 'en',
  zh: 'zh_hans',
} as const;

/**
 * The kitchen's own reason a dish is not available, as a message key.
 *
 * INTEGRATION · this map existed five times, in five screens, under two
 * different names (`REASON_KEY` in Q3 and Q5, `REASON_LABEL` in Q4, Q7 and
 * Q14). Five copies of one mapping is five places to forget when the contract
 * adds a sixth `UnavailabilityReason` — and the failure mode is a guest being
 * told nothing about why their food vanished, which is the exact thing the
 * one-menu 86 rule exists to prevent. It lives here now, beside the keys it
 * names, and every screen reads it.
 *
 * Typed against `UnavailabilityReason` structurally rather than by importing
 * the API types, so `src/i18n` keeps having no dependency on `src/api`.
 */
export const REASON_KEY = {
  eighty_six: 'reason.eighty_six',
  out_of_stock: 'reason.out_of_stock',
  off_schedule: 'reason.off_schedule',
  channel_hidden: 'reason.channel_hidden',
  deactivated: 'reason.deactivated',
} as const satisfies Record<string, MessageKey>;

/**
 * The key for a reason the projection gave, or `null` when it gave none.
 *
 * A reason this client does not recognise returns null rather than throwing or
 * rendering a raw enum: the contract says to tolerate unknown values, and a
 * caller then falls back to `common.soldOut`, which is true regardless.
 */
export function reasonMessageKey(reason: string | null | undefined): MessageKey | null {
  if (!reason) return null;
  return (REASON_KEY as Record<string, MessageKey>)[reason] ?? null;
}
